import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { ValueOf } from '#layers/base/public';
import type { FormSchema, FormSchemaResolved } from '#layers/block-engine/public';
import type { TranslationPlaceholder } from '#layers/i18n/public';

export const AuthProtocol = {
	Password: 'password',
	OAuth2: 'oauth2',
} as const;
export type AuthProtocol = ValueOf<typeof AuthProtocol>;

type GetBaseFormValues<TFormValues, TExtends> = TExtends extends { '~formValues': infer BaseForm }
	? BaseForm extends Record<string, any> ? BaseForm : TFormValues
	: TFormValues;

export interface AuthProviderFormSchemaContext<
	TBaseFormValues extends Record<string, any> = Record<string, any>,
> {
	base?: {
		resolvedOptions: FormSchemaResolved<TBaseFormValues>;
		schema: StandardSchemaV1 | undefined;
	};
}

export interface AuthProviderWalkthroughStep {
	key: string;
	href?: string;
	showCallbackUrl?: boolean;
}

export interface ProviderIcon {
	default: string;
	dark?: string;
}

/**
 * Slim shape shared by login provider choosers and provider previews.
 */
export interface ChooserEntry {
	apiName: string;
	protocol: string;
	preset?: string | null;
	displayName?: string | null;
	icon?: string | null;
	iconColor?: string | null;
	def?: AuthProviderDefinition;
}

/**
 * An auth provider definition.
 *
 * `id` is the registry key. For base protocol entries it equals the protocol
 * value (e.g. `'oauth2'`, `'password'`). For presets, it equals the BE `preset`
 * column value (e.g. `'google'`, `'github'`).
 *
 * The definition owns the `config` shape. `defineAuthProvider` prepends a
 * shared identity group (`apiName`, `enabled`) to the form schema of base
 * protocols so the wizard renders one unified form; presets inherit identity
 * via `context.base.resolvedOptions`. The wire envelope (`apiName`, `enabled`,
 * `protocol`, `preset`) is still assembled at submit time — identity values
 * are split off the form state before posting.
 *
 * `TConfig` is the wire shape sent to the BE. `TFormValues` is the form-side
 * shape (defaults to `TConfig`). They differ when a form block produces a
 * different shape than the wire schema (e.g. `InputRepeater` returns an array
 * but the wire wants a dict). Use `transformOptions` to bridge.
 */
interface AuthProviderConfig<
	TConfig extends Record<string, any>,
	TFormValues extends Record<string, any>,
	TExtends extends AuthProviderDefinition<any, any, any> | undefined,
> {
	id: string;
	name: TranslationPlaceholder;
	description?: TranslationPlaceholder;
	protocol: AuthProtocol;
	icon?: string | ProviderIcon;
	iconColor?: string;

	schema?: StandardSchemaV1;
	/**
	 * Validation schema used in edit mode. Falls back to `schema` when omitted.
	 * Use when the create-time schema has stricter requirements than PATCH —
	 * e.g. `clientSecret` is required on POST but optional on PATCH because the
	 * BE keeps the stored value when the key is omitted.
	 */
	editSchema?: StandardSchemaV1;
	extends?: TExtends;

	/**
	 * Optional link to the provider's official OAuth 2 setup docs. Surfaced as
	 * a "Setup guide →" link inside the wizard's walkthrough panel.
	 */
	docsUrl?: string;

	/**
	 * Optional ordered list of i18n keys for provider-specific walkthrough
	 * steps. When omitted, the wizard renders the generic step set.
	 */
	walkthroughStepKeys?: Array<string | AuthProviderWalkthroughStep>;

	options?: FormSchema<
		TFormValues,
		AuthProviderFormSchemaContext<GetBaseFormValues<TFormValues, TExtends>>
	>;

	/**
	 * Transform form values to the BE wire `config` shape on save. Use when a
	 * form block (e.g. `InputRepeater` for dicts) produces a different shape
	 * than the wire schema expects.
	 */
	transformOptions?: (form: TFormValues) => TConfig;

	/**
	 * Inverse of `transformOptions`. Used by the edit page to seed the form from
	 * a BE record. Required on every protocol that defines `transformOptions`,
	 * otherwise edit prefill will be lossy (e.g. repeater rows go missing).
	 * Returns a partial since some wire fields are write-only (clientSecret).
	 */
	parseInitialValues?: (config: TConfig) => Partial<TFormValues>;
}

export type AuthProviderDefinition<
	TConfig extends Record<string, any> = any,
	TFormValues extends Record<string, any> = TConfig,
	TExtends extends AuthProviderDefinition<any, any, any> | undefined = undefined,
> = AuthProviderConfig<TConfig, TFormValues, TExtends> & {
	'~config': TConfig;
	'~formValues': TFormValues;
};

export function defineAuthProvider<
	TExtends extends AuthProviderDefinition<any, any, any>,
	TConfig extends Record<string, any> = TExtends['~config'],
	TFormValues extends Record<string, any> = TExtends['~formValues'],
>(
	cfg: AuthProviderConfig<TConfig, TFormValues, TExtends> & { extends: TExtends },
): AuthProviderDefinition<TConfig, TFormValues, TExtends>;

export function defineAuthProvider<
	TConfig extends Record<string, any>,
	TFormValues extends Record<string, any> = TConfig,
>(
	cfg: AuthProviderConfig<TConfig, TFormValues, undefined> & { extends?: never },
): AuthProviderDefinition<TConfig, TFormValues>;

export function defineAuthProvider(cfg: any): any {
	// Pass-through. Identity-field injection now lives in
	// `prependIdentityFields` (see `./identity.ts`) and is applied at registry
	// assembly in `./index.ts`. Keeping this function lets callers use a
	// dedicated helper that drives the discriminated overloads above.
	return cfg;
}
